import { createDemoDevice, type CommandOutcome, type DeviceState, type HeadId, type SettingDescriptor } from "./model";
import type { BleTransport } from "./transport";

export type RemoteEvent =
  | { type: "state"; device: DeviceState }
  | { type: "operation"; requestId: number; outcome: CommandOutcome }
  | { type: "operation_verifying"; requestId: number; recovery: Promise<void> }
  | { type: "disconnected"; reason?: string };

export interface RemoteApi {
  readonly connected: boolean;
  readonly canReconnect: boolean;
  readonly deviceId?: string;
  readonly enrollmentControllerId?: string;
  pair(): Promise<DeviceState>;
  reconnect(preferredDeviceId?: string): Promise<DeviceState>;
  enroll(accessCode: string): Promise<DeviceState>;
  disconnect(): void;
  sync(): Promise<DeviceState>;
  saveSnapshot(head: HeadId, name: string, replaceId?: number): Promise<CommandOutcome>;
  loadSnapshot(id: number): Promise<CommandOutcome>;
  unloadSnapshot(): Promise<CommandOutcome>;
  renameSnapshot(id: number, name: string): Promise<CommandOutcome>;
  deleteSnapshot(id: number): Promise<CommandOutcome>;
  clearGraph(head: HeadId): Promise<CommandOutcome>;
  runDemo(head: HeadId): Promise<CommandOutcome>;
  saveSettings(settings: SettingDescriptor[]): Promise<CommandOutcome>;
  resetBatch(): Promise<CommandOutcome>;
  repairStorage(): Promise<CommandOutcome>;
  confirm(token: bigint): Promise<CommandOutcome>;
  cancel(token: bigint): Promise<CommandOutcome>;
  onEvent(listener: (event: RemoteEvent) => void): () => void;
}

function outcome(code: CommandOutcome["code"], message?: string): CommandOutcome {
  return { code, message };
}

export class MockRemoteApi implements RemoteApi {
  #connected = false;
  #device = createDemoDevice();
  #listeners = new Set<(event: RemoteEvent) => void>();
  #demoTimers = new Map<HeadId, number>();
  #pendingConfirm = new Map<bigint, () => void>();

  get connected(): boolean {
    return this.#connected;
  }

  get canReconnect(): boolean {
    return true;
  }

  get deviceId(): string | undefined {
    return "mock-espress9th";
  }

  get enrollmentControllerId(): string | undefined { return undefined; }

  async pair(): Promise<DeviceState> {
    await new Promise((resolve) => window.setTimeout(resolve, 350));
    this.#connected = true;
    return this.sync();
  }

  async reconnect(): Promise<DeviceState> {
    return this.pair();
  }

  async enroll(): Promise<DeviceState> { return this.sync(); }

  disconnect(reason?: string): void {
    this.#connected = false;
    for (const timer of this.#demoTimers.values()) window.clearInterval(timer);
    this.#demoTimers.clear();
    for (const head of [1, 2] as const) {
      if (!this.#device.heads[head].demoActive) continue;
      this.#device.heads[head] = {
        ...this.#device.heads[head],
        phase: "complete",
        status: "last",
        detectorActive: false,
        demoActive: false,
      };
    }
    this.#emit({ type: "disconnected", reason });
  }

  async sync(): Promise<DeviceState> {
    if (!this.#connected) throw new Error("Mock controller is disconnected");
    return structuredClone(this.#device);
  }

  #mutate(change: (device: DeviceState) => void): void {
    change(this.#device);
    this.#device.revision += 1;
    this.#emit({ type: "state", device: structuredClone(this.#device) });
  }

  async saveSnapshot(head: HeadId, name: string, replaceId?: number): Promise<CommandOutcome> {
    if (!name.trim()) return outcome("invalid", "Enter a snapshot name.");
    if (this.#device.snapshots.length >= this.#device.capabilities.maxSnapshots && replaceId === undefined) {
      return this.#requireConfirm(
        "Replace the oldest snapshot?",
        "Storage is full. The oldest snapshot will be replaced by this capture.",
        () => void this.saveSnapshot(head, name, this.#device.snapshots[0]?.id),
      );
    }
    this.#mutate((device) => {
      if (replaceId !== undefined) device.snapshots = device.snapshots.filter((snapshot) => snapshot.id !== replaceId);
      const source = device.heads[head];
      device.snapshots.push({
        id: Math.max(1000, ...device.snapshots.map((snapshot) => snapshot.id)) + 1,
        name: name.trim(),
        capturedShotNumber: device.lifetimeShots,
        volumeMl: source.volumeMl,
        durationMs: source.elapsedMs,
        sampleCount: source.trace.points.length,
        classification: source.trace.classification ?? "SHOT",
        score: source.score,
        active: false,
      });
    });
    return outcome("persistence_pending", "Snapshot accepted; storage write is finishing.");
  }

  async loadSnapshot(id: number): Promise<CommandOutcome> {
    if (!this.#device.snapshots.some((snapshot) => snapshot.id === id)) return outcome("not_found");
    this.#mutate((device) => {
      device.snapshots = device.snapshots.map((snapshot) => ({ ...snapshot, active: snapshot.id === id }));
      device.selectedSnapshotId = id;
    });
    return outcome("ok", "Reference loaded.");
  }

  async unloadSnapshot(): Promise<CommandOutcome> {
    this.#mutate((device) => {
      device.snapshots = device.snapshots.map((snapshot) => ({ ...snapshot, active: false }));
      device.selectedSnapshotId = undefined;
    });
    return outcome("ok", "Reference unloaded.");
  }

  async renameSnapshot(id: number, name: string): Promise<CommandOutcome> {
    if (!name.trim()) return outcome("invalid", "Enter a snapshot name.");
    this.#mutate((device) => {
      device.snapshots = device.snapshots.map((snapshot) =>
        snapshot.id === id ? { ...snapshot, name: name.trim() } : snapshot,
      );
    });
    return outcome("ok", "Snapshot renamed.");
  }

  async deleteSnapshot(id: number): Promise<CommandOutcome> {
    const snapshot = this.#device.snapshots.find((candidate) => candidate.id === id);
    if (!snapshot) return outcome("not_found");
    return this.#requireConfirm(
      `Delete "${snapshot.name}"?`,
      "This removes the stored reference from the controller.",
      () => this.#mutate((device) => {
        device.snapshots = device.snapshots.filter((candidate) => candidate.id !== id);
        if (device.selectedSnapshotId === id) device.selectedSnapshotId = undefined;
      }),
    );
  }

  async clearGraph(head: HeadId): Promise<CommandOutcome> {
    if (["extracting", "prewet", "demo"].includes(this.#device.heads[head].phase)) {
      return outcome("busy", "Wait for this head to become idle.");
    }
    this.#mutate((device) => {
      device.heads[head] = { ...device.heads[head], volumeMl: 0, elapsedMs: 0, score: undefined, trace: { points: [] }, pendingSave: false };
    });
    return outcome("ok", `Head ${head} graph cleared.`);
  }

  async runDemo(head: HeadId): Promise<CommandOutcome> {
    if (!this.#device.capabilities.demo) return outcome("invalid", "Enable developer mode first.");
    if (this.#device.heads[head].phase !== "idle" && this.#device.heads[head].phase !== "complete") {
      return outcome("busy", "This head is already active.");
    }
    const existing = this.#demoTimers.get(head);
    if (existing) window.clearInterval(existing);
    let tick = 0;
    this.#mutate((device) => {
      device.heads[head] = { ...device.heads[head], phase: "demo", status: "live", reasonFlags: 0, detectorActive: true, demoActive: true, saveAvailable: true, volumeMl: 0, elapsedMs: 0, score: undefined, trace: { points: [] } };
    });
    const timer = window.setInterval(() => {
      if (!this.#connected) {
        window.clearInterval(timer);
        this.#demoTimers.delete(head);
        return;
      }
      tick += 1;
      const elapsedMs = tick * 250;
      const rate = tick < 10 ? 0.28 + tick * 0.08 : tick < 90 ? 0.88 + Math.sin(tick / 7) * 0.04 : Math.max(0, 0.88 - (tick - 90) * 0.08);
      this.#mutate((device) => {
        const current = device.heads[head];
        const volumeMl = Math.min(42, current.volumeMl + rate);
        const points = [...current.trace.points, { elapsedMs, milliliters: volumeMl }];
        device.heads[head] = {
          ...current,
          phase: tick >= 101 ? "complete" : "demo",
          status: tick >= 101 ? "last" : "live",
          detectorActive: tick < 101,
          demoActive: tick < 101,
          saveAvailable: true,
          elapsedMs,
          volumeMl,
          score: tick >= 101 ? 92 : undefined,
          trace: { points, plateauStartMs: 2500, plateauEndMs: 22500, classification: tick >= 101 ? "SHOT" : undefined },
        };
      });
      if (tick >= 101) {
        window.clearInterval(timer);
        this.#demoTimers.delete(head);
      }
    }, 250);
    this.#demoTimers.set(head, timer);
    return outcome("ok", `Demo started on head ${head}.`);
  }

  async saveSettings(settings: SettingDescriptor[]): Promise<CommandOutcome> {
    this.#mutate((device) => {
      device.settings = settings.map((setting) => ({ ...setting }));
      const developer = settings.find((setting) => setting.key === "developerMode");
      device.capabilities.demo = Boolean(developer?.value);
    });
    return outcome("persistence_pending", "Settings applied; storage write is finishing.");
  }

  async resetBatch(): Promise<CommandOutcome> {
    return this.#requireConfirm("Reset batch counter?", "Lifetime shots are not affected.", () => {
      this.#mutate((device) => { device.batchShots = 0; });
    });
  }

  async repairStorage(): Promise<CommandOutcome> {
    if (!this.#device.storageNeedsRepair) return outcome("invalid", "Snapshot storage is healthy; repair is not available.");
    return this.#requireConfirm(
      "Repair snapshot storage?",
      "All snapshots will be erased. Detector settings and counters are preserved.",
      () => this.#mutate((device) => { device.snapshots = []; device.selectedSnapshotId = undefined; device.storageAvailable = true; device.storageNeedsRepair = false; }),
    );
  }

  #requireConfirm(title: string, detail: string, action: () => void): CommandOutcome {
    const token = BigInt(Math.floor(Math.random() * 0x7fffffff));
    this.#pendingConfirm.set(token, action);
    window.setTimeout(() => this.#pendingConfirm.delete(token), 30_000);
    return {
      code: "confirmation_required",
      confirmation: { token, expiresAt: Date.now() + 30_000, title, detail, destructive: true },
    };
  }

  async confirm(token: bigint): Promise<CommandOutcome> {
    const action = this.#pendingConfirm.get(token);
    if (!action) return outcome("conflict", "Confirmation expired. Refresh and try again.");
    this.#pendingConfirm.delete(token);
    action();
    return outcome("ok", "Confirmed.");
  }

  async cancel(token: bigint): Promise<CommandOutcome> {
    this.#pendingConfirm.delete(token);
    return outcome("ok");
  }

  onEvent(listener: (event: RemoteEvent) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(event: RemoteEvent): void {
    for (const listener of this.#listeners) listener(event);
  }
}

// The binary implementation is defined in ble-remote-api.ts. Keeping this constructor
// here makes the dependency boundary explicit for tests and mock UI development.
export type TransportFactory = () => BleTransport;
